import { useCallback, useRef } from "react";

/**
 * useKidVoice — speaks short phrases out loud using the Web Speech API,
 * tuned to sound bright and child-friendly (higher pitch, slightly slower).
 * Picks the most kid/female-sounding voice available on the device.
 */
export default function useKidVoice() {
  const voiceRef = useRef(null);

  const pickVoice = useCallback(() => {
    if (!("speechSynthesis" in window)) return null;
    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) return null;

    // Preference order: explicit child voices first (these sound like a kid),
    // then bright young-sounding voices, then any en female, then any en.
    const byName = (re) => voices.find(v => re.test(v.name));
    const enVoices = voices.filter(v => /en[-_]/i.test(v.lang) || /english/i.test(v.name));

    voiceRef.current =
      byName(/Google US English/i) ||                        // warm, natural, human-sounding network voice — best quality on Chrome
      byName(/Google UK English Female/i) ||                 // natural British female fallback
      byName(/Microsoft (Aria|Jenny|Michelle|Ava)/i) ||      // modern natural Windows voices (Edge)
      byName(/(Samantha|Karen|Tessa|Moira)/i) ||             // natural Apple/macOS female voices
      byName(/Zira/i) ||                                     // last-resort robotic Windows fallback
      enVoices.find(v => /female/i.test(v.name)) ||
      enVoices[0] ||
      voices[0];

    return voiceRef.current;
  }, []);

  const speak = useCallback((text, { pitch = 1.1, rate = 0.95, volume = 1 } = {}) => {
    if (!text || !("speechSynthesis" in window)) return;
    if (localStorage.getItem("kids_sound_muted") === "1") return;
    try {
      const synth = window.speechSynthesis;
      // Chrome can leave the engine "paused" after a previous cancel — resume it.
      if (synth.paused) synth.resume();

      const voice = voiceRef.current || pickVoice();
      const u = new SpeechSynthesisUtterance(text);
      if (voice) { u.voice = voice; u.lang = voice.lang; }
      u.pitch = pitch;   // higher = more childlike
      u.rate = rate;     // a touch slower so kids can follow
      u.volume = volume;

      const busy = synth.speaking || synth.pending;
      if (busy) {
        // Something is already talking. Cancel it, then speak on the next tick
        // (Chrome swallows a cancel-then-speak issued in the same tick).
        synth.cancel();
        setTimeout(() => { try { synth.resume(); synth.speak(u); } catch { /* ignore */ } }, 0);
      } else {
        // Nothing playing: speak SYNCHRONOUSLY. iOS Safari only allows speech
        // that starts inside the user gesture itself — a setTimeout here would
        // break the user-activation chain and stay silent on iPad/iPhone.
        synth.speak(u);
      }
    } catch { /* speech not available — silently ignore */ }
  }, [pickVoice]);

  const cancel = useCallback(() => {
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
  }, []);

  // Voices load asynchronously in some browsers; warm them up.
  if (typeof window !== "undefined" && "speechSynthesis" in window && !voiceRef.current) {
    window.speechSynthesis.onvoiceschanged = pickVoice;
    pickVoice();
  }

  return { speak, cancel };
}
